import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { theme } from '../theme/theme'
import { BodySmall, Title6 } from './styles/Typography'
import { VideoCamera } from './icons/VideoCamera'
import { Calendar } from './icons/Calendar'
import { Pin } from './icons/Pin'
import { Arrow } from './icons'
import { useWindowWidth } from '../hooks/useWindowWidth'
import { isTablet, isMobile } from '../utils/devices'
import { HealthCallAppointment } from '../api/ester-b2b-api/private/healthCalls/types'
import { formatDisplayStartTime } from '../utils/dates'

interface DigitalHealthConsultationBannerProps {
  appointment: HealthCallAppointment
  linkUrl: string
}

export const DigitalHealthConsultationBanner = ({ appointment, linkUrl }: DigitalHealthConsultationBannerProps) => {
  const { t, i18n } = useTranslation()
  const width = useWindowWidth()
  const isMobileDevice = isTablet(width) || isMobile(width)
  const iconSize = isMobileDevice ? 18 : 24
  const dateTime = formatDisplayStartTime(appointment, i18n.language, t)

  return (
    <Container>
      <ContentWrapper>
        <IconContainer $isMobileDevice={isMobileDevice}>
          <VideoCamera size={iconSize} color={theme.color.white} />
        </IconContainer>
        <TextContent>
          <StyledTitle>{t('employee.home.digitalHealthConsultationTitle')}</StyledTitle>
          <InfoRow>
            <InfoItem>
              <Calendar size={20} color={theme.color.white} />
              <InfoText>{dateTime}</InfoText>
            </InfoItem>
            <InfoItem>
              <Pin size={20} color={theme.color.white} />
              <InfoText>{t('employee.healthCall.bookingInfo')}</InfoText>
            </InfoItem>
          </InfoRow>
          <StyledLink to={linkUrl}>
            {t('employee.home.viewBookingDetails')}
            <Arrow size={20} color={theme.color.white} />
          </StyledLink>
        </TextContent>
      </ContentWrapper>
    </Container>
  )
}

const Container = styled.div`
  background-color: ${theme.color.blueDark};
  border-radius: 16px;
  padding: ${theme.spacing.large}px;
`

const ContentWrapper = styled.div`
  display: flex;
  gap: ${theme.spacing.medium}px;
  align-items: flex-start;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    flex-direction: column;
  }
`

const IconContainer = styled.div<{ $isMobileDevice: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ $isMobileDevice }) => ($isMobileDevice ? 32 : 40)}px;
  height: ${({ $isMobileDevice }) => ($isMobileDevice ? 32 : 40)}px;
  min-width: ${({ $isMobileDevice }) => ($isMobileDevice ? 32 : 40)}px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
`

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.small}px;
  flex: 1;
`

const StyledTitle = styled(Title6)`
  color: ${theme.color.white};
  margin: 0;
`

const InfoRow = styled.div`
  display: flex;
  gap: ${theme.spacing.mediumLarge}px;
  flex-wrap: wrap;

  @media screen and (max-width: ${theme.breakpoint.large}px) {
    flex-direction: column;
    gap: ${theme.spacing.small}px;
  }
`

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xsmall}px;
`

const InfoText = styled(BodySmall)`
  color: ${theme.color.white};
  margin: 0;
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xsmall}px;
  color: ${theme.color.white};
  text-decoration: none;
  font-size: ${theme.fontSize.small}px;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`
